import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { closeOpenSaltclickConversations } from './closeSaltclickConversations';
import {
  clearSaltclickSession,
  endSaltclickChatSession,
  getSaltclickVerifiedForUserId,
  setSaltclickVerifiedForUserId,
} from './saltclickChatSession';

/**
 * Takes the conversation down on logout, not just the session.
 *
 * `clearSession(true)` on its own — what logout used to do — is the widget's `endSession`
 * and nothing more: the session goes, the conversation stays open server-side. Which
 * leaves the state there is no way forward from, because the widget resumes any open
 * conversation as it boots and then paints the ended screen. Nothing else clears it: the
 * portal closes a conversation when the user ends a chat, and a user who logged out did
 * not end theirs.
 *
 * So it happens here, and it has to happen here, because this is the last moment the
 * token that owns the conversation can be minted. A different account logging in next
 * could not do it on their behalf — the conversation is not theirs, and Salesforce
 * answers `403` to say so.
 *
 * Order matters twice over. The close goes first, while the portal's own access token is
 * still valid, so this must run BEFORE the logout request rather than after it. And the
 * clear that follows passes `false`: the session went with the conversation, so asking
 * the widget to end it again is a 403 on something that no longer exists.
 *
 * Never rejects and never blocks: logout is not allowed to fail, or wait, because
 * Salesforce did.
 */
export const retireSaltclickChatOnLogout = async (): Promise<void> => {
  try {
    // Nothing was ever verified in this document, so there is no conversation of ours to
    // close — and no reason to spend three calls on Salesforce for a logout by someone
    // who never opened the chat. A conversation cannot exist without a verified session
    // on this deployment (`authMode: "Auth"`), so the marker is the whole question.
    if (!getSaltclickVerifiedForUserId()) return;

    const { orgId, deploymentName, scrt2Url } = RemoteConfigUtils.getSaltclickChatConfig() ?? {};
    if (!orgId || !deploymentName || !scrt2Url) {
      console.error(
        '[Saltclick] Logging out with an incomplete SALTCLICK_CHAT_CONFIG — ending the ' +
          'session without closing the conversation, which the next login will resume',
      );
      await endSaltclickChatSession();

      return;
    }

    try {
      await closeOpenSaltclickConversations({ scrt2Url, orgId, deploymentName });
    } catch (err) {
      // The conversation is still open, so the session it belongs to is genuinely live and
      // has to be ended the ordinary way — `clearSession(true)`. Falling through to the
      // `false` clear below would leave both standing.
      console.error('[Saltclick] Could not close the conversation on logout: ', err);
      await endSaltclickChatSession();

      return;
    }

    if (await clearSaltclickSession(false)) {
      setSaltclickVerifiedForUserId(undefined);

      return;
    }

    // The marker stays on an unconfirmed clear, as it does everywhere else: it is the
    // only record that this document's widget still holds a verified session, and the
    // next login reads it to decide whether a handover is needed.
    console.error(
      '[Saltclick] Closed the conversation on logout but could not confirm the session ' +
        'clear — the next login will hand the widget over instead',
    );
  } catch (err) {
    // Belt and braces on a promise nobody awaits: an unhandled rejection here would
    // surface as a logout error with no relation to logging out.
    console.error('[Saltclick] Could not retire the chat on logout: ', err);
  }
};
